import React, {useEffect, useMemo, useState} from 'react'
import {
	Alert,
	Box,
	Button,
	Chip,
	CircularProgress,
	Dialog,
	DialogActions,
	DialogContent,
	DialogTitle,
	FormControl,
	IconButton,
	InputLabel,
	MenuItem,
	Select,
	Table,
	TableBody,
	TableCell,
	TableHead,
	TableRow,
	Tooltip,
	Typography,
	useTheme,
} from '@mui/material'
import {Check, ContentCopy}     from '@mui/icons-material'
import {doGet}                 from '../../util/do-fetch'
import {diffLines, alignRows, diffStats} from '../../util/diff-utils'

/*
 * Change history for one config key, and a diff between any two of its versions (088).
 *
 * Versions arrive newest-first with their values attached, so switching which two versions are
 * compared is instant -- no request per selection. The list is small by nature: a config key is
 * edited a handful of times a year.
 */

const changeTypeColor = {
	create: 'success',
	update: 'default',
	delete: 'error',
	backfill: 'default',
}

const formatWhen = (iso) => {
	if (!iso) return ''
	const date = new Date(iso)
	return isNaN(date) ? '' : date.toLocaleString()
}

const ConfigVersionHistory = ({configKey, open, onClose}) => {
	const theme = useTheme()
	const [versions, setVersions] = useState(null)
	const [error, setError] = useState(null)
	const [fromVersion, setFromVersion] = useState('')
	const [toVersion, setToVersion] = useState('')
	// the version whose value was just copied, so the button can confirm it happened
	const [copiedVersion, setCopiedVersion] = useState(null)

	useEffect(() => {
		if (!open || !configKey) return

		let cancelled = false
		setVersions(null)
		setError(null)
		setCopiedVersion(null)

		const fetchVersions = async () => {
			try {
				const response = await doGet(`/admin/app-config/${encodeURIComponent(configKey)}/versions`)
				if (!response.ok) throw new Error('Could not load the change history.')

				const data = await response.json()
				if (cancelled) return

				setVersions(data)
				/*
				 * Default to the comparison anyone opening this actually wants: what the most
				 * recent change did. With only one version there is nothing to compare, and the
				 * pickers stay on that single version.
				 */
				setToVersion(data.length ? data[0].version : '')
				setFromVersion(data.length > 1 ? data[1].version : (data.length ? data[0].version : ''))
			}
			catch (err) {
				if (!cancelled) setError(err.message || 'Could not load the change history.')
			}
		}

		fetchVersions()
		return () => { cancelled = true }
	}, [open, configKey])

	const valueOf = (version) => {
		const found = versions && versions.find(v => v.version === version)
		return found ? found.value : ''
	}

	const rows = useMemo(
		() => (versions && versions.length ? diffLines(valueOf(fromVersion), valueOf(toVersion)) : []),
		// eslint-disable-next-line react-hooks/exhaustive-deps
		[versions, fromVersion, toVersion]
	)
	const stats = useMemo(() => diffStats(rows), [rows])
	const pairs = useMemo(() => alignRows(rows), [rows])

	// one side of a pair: red where a line was taken away, green where one arrived, and both on a
	// changed line, so the eye reads a replacement as a single row rather than two events
	const removedBg = theme.palette.mode === 'dark' ? 'rgba(248,113,113,0.18)' : 'rgba(220,38,38,0.10)'
	const addedBg = theme.palette.mode === 'dark' ? 'rgba(16,185,129,0.18)' : 'rgba(16,185,129,0.14)'

	const sideBg = (pair, side) => {
		if (pair.type === 'same') return 'transparent'
		if (side === 'left') return pair.left ? removedBg : 'transparent'
		return pair.right ? addedBg : 'transparent'
	}

	/*
	 * The highest version is the live one -- unless the last thing that happened to the key was a
	 * delete, in which case there is no live row and nothing should claim to be current.
	 */
	const currentVersion = versions && versions.length && versions[0].change_type !== 'delete'
		? versions[0].version
		: null

	const handleCopy = async (version) => {
		try {
			await navigator.clipboard.writeText(valueOf(version) || '')
			setCopiedVersion(version)
			setTimeout(() => setCopiedVersion(current => (current === version ? null : current)), 2000)
		}
		catch (err) {
			setError('Could not copy to the clipboard.')
		}
	}

	const versionLabel = (v) => `v${v.version}${v.version === currentVersion ? ' [CURRENT]' : ''} — ${formatWhen(v.changed_at)}${v.changed_by_email ? ` — ${v.changed_by_email}` : ''}`

	return (
		<Dialog open={open} onClose={onClose} fullWidth maxWidth="lg">
			<DialogTitle sx={{fontFamily: 'monospace'}}>{configKey}</DialogTitle>
			<DialogContent dividers>
				{error && <Alert severity="error" sx={{mb: 2}}>{error}</Alert>}

				{!versions && !error && (
					<Box display="flex" justifyContent="center" py={4}><CircularProgress size={24}/></Box>
				)}

				{versions && versions.length === 0 && (
					<Alert severity="info">
						No history yet. Versions are recorded from the next change made through this screen.
					</Alert>
				)}

				{versions && versions.length > 0 && (
					<>
						<Typography variant="subtitle2" sx={{mb: 1}}>Change history</Typography>
						<Table size="small" sx={{mb: 3}}>
							<TableHead>
								<TableRow>
									<TableCell><b>Version</b></TableCell>
									<TableCell><b>When</b></TableCell>
									<TableCell><b>Who</b></TableCell>
									<TableCell><b>Change</b></TableCell>
									<TableCell><b>Value</b></TableCell>
								</TableRow>
							</TableHead>
							<TableBody>
								{versions.map(v => (
									<TableRow key={v.version} hover>
										<TableCell sx={{whiteSpace: 'nowrap'}}>
											v{v.version}
											{v.version === currentVersion && (
												<Chip size="small"
												      label="CURRENT"
												      color="primary"
												      variant="outlined"
												      sx={{ml: 1, height: 20}}
												/>
											)}
										</TableCell>
										<TableCell>{formatWhen(v.changed_at)}</TableCell>
										{/* a backfilled or internal change genuinely has no author -- say so
										    rather than attributing it to someone */}
										<TableCell>{v.changed_by_email || <em>unknown</em>}</TableCell>
										<TableCell>
											<Chip size="small"
											      label={v.change_type}
											      color={changeTypeColor[v.change_type] || 'default'}
											/>
										</TableCell>
										{/*
										  * Copies that version's text to the clipboard so an older
										  * value can be pasted straight over the live one in the
										  * editor. Deliberately not a one-click "restore": the
										  * paste goes through the normal edit and save, so the
										  * revert is recorded as a change like any other.
										  */}
										<TableCell>
											<Tooltip title={copiedVersion === v.version ? 'Copied' : `Copy v${v.version} to the clipboard`}>
												<IconButton size="small" onClick={() => handleCopy(v.version)}>
													{copiedVersion === v.version
														? <Check fontSize="small" color="success"/>
														: <ContentCopy fontSize="small"/>}
												</IconButton>
											</Tooltip>
										</TableCell>
									</TableRow>
								))}
							</TableBody>
						</Table>

						<Box display="flex" gap={2} alignItems="center" flexWrap="wrap" sx={{mb: 2}}>
							<FormControl size="small" sx={{minWidth: 280}}>
								<InputLabel>Compare</InputLabel>
								<Select label="Compare" value={fromVersion}
								        onChange={(e) => setFromVersion(e.target.value)}>
									{versions.map(v => (
										<MenuItem key={v.version} value={v.version}>{versionLabel(v)}</MenuItem>
									))}
								</Select>
							</FormControl>

							<FormControl size="small" sx={{minWidth: 280}}>
								<InputLabel>With</InputLabel>
								<Select label="With" value={toVersion}
								        onChange={(e) => setToVersion(e.target.value)}>
									{versions.map(v => (
										<MenuItem key={v.version} value={v.version}>{versionLabel(v)}</MenuItem>
									))}
								</Select>
							</FormControl>

							<Box sx={{color: 'text.secondary', fontSize: '.9em'}}>
								<span style={{color: theme.custom.status.greenStrong}}>+{stats.added}</span>
								{' / '}
								<span style={{color: theme.custom.status.danger}}>-{stats.removed}</span>
							</Box>
						</Box>

						{stats.added === 0 && stats.removed === 0 ? (
							<Alert severity="info">These two versions are identical.</Alert>
						) : (
							<Box sx={{
								border: `1px solid ${theme.palette.divider}`,
								borderRadius: 1,
								overflow: 'auto',
								maxHeight: '45vh',
								fontFamily: 'monospace',
								fontSize: '.85rem',
							}}>
								{/* column headings name which version is on which side, so a diff
								    read out of context cannot be taken the wrong way round */}
								<Box sx={{
									display: 'flex',
									position: 'sticky',
									top: 0,
									zIndex: 1,
									backgroundColor: theme.palette.background.paper,
									borderBottom: `1px solid ${theme.palette.divider}`,
									fontWeight: 600,
								}}>
									<Box sx={{flex: 1, minWidth: 0, px: 1, py: .5, borderRight: `1px solid ${theme.palette.divider}`}}>
										v{fromVersion}{fromVersion === currentVersion && ' [CURRENT]'}
									</Box>
									<Box sx={{flex: 1, minWidth: 0, px: 1, py: .5}}>
										v{toVersion}{toVersion === currentVersion && ' [CURRENT]'}
									</Box>
								</Box>

								{pairs.map((pair, i) => (
									<Box key={i} sx={{display: 'flex', alignItems: 'stretch'}}>
										<DiffSide line={pair.left}
										          marker={pair.left && pair.type !== 'same' ? '-' : ''}
										          background={sideBg(pair, 'left')}
										          divider={theme.palette.divider}
										          borderRight
										/>
										<DiffSide line={pair.right}
										          marker={pair.right && pair.type !== 'same' ? '+' : ''}
										          background={sideBg(pair, 'right')}
										          divider={theme.palette.divider}
										/>
									</Box>
								))}
							</Box>
						)}

					</>
				)}
			</DialogContent>
			<DialogActions>
				<Button onClick={onClose}>Close</Button>
			</DialogActions>
		</Dialog>
	)
}

/*
 * One half of a side-by-side row. Text wraps rather than scrolling horizontally: the two panes
 * stay locked to the same rows that way, and a wrapped prompt line is still readable.
 */
const DiffSide = ({line, marker, background, divider, borderRight}) => (
	<Box sx={{
		flex: 1,
		minWidth: 0,
		display: 'flex',
		backgroundColor: background,
		borderRight: borderRight ? `1px solid ${divider}` : 'none',
	}}>
		<Box sx={{width: 40, flexShrink: 0, textAlign: 'right', pr: 1, color: 'text.disabled', userSelect: 'none'}}>
			{line ? (line.beforeLine || line.afterLine) : ''}
		</Box>
		<Box sx={{width: 14, flexShrink: 0, textAlign: 'center', color: 'text.secondary', userSelect: 'none'}}>
			{marker}
		</Box>
		<Box sx={{flex: 1, minWidth: 0, whiteSpace: 'pre-wrap', wordBreak: 'break-word', pr: 1}}>
			{line ? (line.text || ' ') : ''}
		</Box>
	</Box>
)

export default ConfigVersionHistory
